// ./src/components/features/Travels/TravelSelector/TravelSelector.tsx
import React, { useMemo, useCallback, useEffect, useRef, useState } from "react";
import {
  Select,
  SelectProps,
  Group,
  Text,
  Stack,
  Box,
  Badge,
  ComboboxProps,
  TextInput,
  CloseButton,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { Travel, TravelFilter } from "@/utils/api/TravelService";

import { useTravels } from "@/hooks/useTravels";

import { useActiveTravelContext } from "@/contexts/ActiveTravelContext";
import { useAppEnvironment } from "@/contexts/AppEnvironmentContext";

import { LoadingIndicator } from "@/components/generic/LoadingIndicator";
import { AppIcon } from "@/components/generic/AppIcon";

import { TravelPickerModal } from "./TravelPickerModal";

// ============================================
// Types
// ============================================

export interface TravelSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange" | "name"> {
  /** Field name for form binding */
  name?: string;

  /** Currently selected travel ID */
  value?: string | null | undefined;

  /** Callback fired when travel selection changes: (name, value, travel?) */
  onChange?: (name: string, value: string | null, travel?: Travel) => void;

  /** Placeholder text when no travel is selected */
  placeholder?: string;

  /** Whether to preselect active travel if available (only in create mode) */
  preselectActiveTravel?: boolean;

  /** Filter travels by specific car ID. When provided, only travels for this car are shown */
  carId?: string | null;

  /** Custom filter function to filter travels */
  travelFilter?: (travel: Travel) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Placeholder text shown while loading */
  loadingPlaceholder?: string;

  /** Text shown when no travels are available */
  emptyText?: string;

  /** Error message to display */
  error?: string | null | undefined;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size of the selector */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Whether to show travel details (car, distance) in dropdown */
  showDetails?: boolean;

  /** Label for the field */
  label?: string;

  /** Whether to show "All Travels" option */
  showAllOption?: boolean;

  /** Label for "All Travels" option */
  allOptionLabel?: string;

  /** Props passed to the underlying Combobox (e.g., withinPortal) */
  comboboxProps?: ComboboxProps;

  /** Whether to render dropdown in portal */
  withinPortal?: boolean;

  /** Current user ID for data loading */
  userId?: string | null;
}

// ============================================
// Option Data Interface
// ============================================

interface TravelOptionData {
  value: string;
  label: string;
  details?: string;
  isActive?: boolean;
  travelType?: string;
  disabled?: boolean;
}

// ============================================
// Constants
// ============================================

const ALL_TRAVELS_VALUE = "__all__";

// ============================================
// Helper Functions
// ============================================

/**
 * Get display name for a travel record
 * Priority: destination > purpose > formatted date
 */
const getTravelDisplayName = (travel: Travel, t: (key: string) => string): string => {
  if (travel.destination) {
    return travel.destination;
  }

  if (travel.purpose) {
    return travel.purpose;
  }

  // Fallback to formatted date
  if (travel.firstDttm) {
    return dayjs(travel.firstDttm).format("MMM D, YYYY HH:mm");
  }

  return t("common:selectors.travel.unnamed");
};

/**
 * Build details string for travel option
 */
const getTravelDetails = (travel: Travel): string | undefined => {
  const parts: string[] = [];

  // Car label
  if (travel.car?.label) {
    parts.push(travel.car.label);
  }

  // Distance
  if (travel.distance !== undefined && travel.distance !== null) {
    const unit = travel.distanceIn || "km";
    parts.push(`${travel.distance} ${unit}`);
  }

  return parts.length > 0 ? parts.join(" • ") : undefined;
};

// ============================================
// Component
// ============================================

const TravelSelectorComponent: React.FC<TravelSelectorProps> = ({
  name = "travelId",
  value,
  onChange,
  placeholder,
  preselectActiveTravel = false,
  carId,
  travelFilter,
  required = false,
  loadingPlaceholder,
  emptyText,
  error,
  disabled,
  size = "md",
  showDetails = true,
  label,
  showAllOption = false,
  allOptionLabel,
  comboboxProps,
  withinPortal,
  userId,
  ...selectProps
}) => {
  const { t } = useTranslation(["common"]);
  const { isMobile } = useAppEnvironment();

  // Get active travel from context
  const { activeTravel, hasActiveTravel } = useActiveTravelContext();

  // Modal state (mobile)
  const [modalOpened, setModalOpened] = useState(false);

  // Build initial filter based on carId
  const initialFilters = useMemo((): Partial<TravelFilter> => {
    const filters: Partial<TravelFilter> = {};

    if (carId) {
      filters.carId = [carId];
    }

    return filters;
  }, [carId]);

  // Use travels hook - sorted by firstDttm DESC (newest first)
  const {
    travels,
    loading,
    lastError,
    getTravelById,
    list,
  } = useTravels({
    autoLoad: false, // We'll manually load when carId is set
    initialFilters,
  });

  // Track if we've done auto-selection
  const hasAutoSelectedRef = useRef(false);

  // Track previous carId to detect changes
  const prevCarIdRef = useRef<string | null | undefined>(undefined);

  // Default translations
  const defaultPlaceholder =
    placeholder || t("common:selectors.travel.placeholder");
  const defaultLabel = label;
  const defaultLoadingPlaceholder =
    loadingPlaceholder || t("common:selectors.travel.loading");
  const defaultEmptyText = emptyText || t("common:selectors.travel.empty");
  const defaultAllOptionLabel =
    allOptionLabel || t("common:selectors.travel.allTravels");

  // Load travels when carId changes or on initial mount
  useEffect(() => {
    const filters: Partial<TravelFilter> = {};
    if (carId) {
      filters.carId = [carId];
    }

    // Check if carId actually changed
    const carIdChanged = prevCarIdRef.current !== carId;
    prevCarIdRef.current = carId;

    // Reset auto-selection when carId changes
    if (carIdChanged) {
      hasAutoSelectedRef.current = false;
    }

    // Always load travels (filtered by carId if provided)
    list(filters, { page: 1, pageSize: 50 }, undefined, true);
  }, [carId, list]);

  // Create a map for quick travel lookup by ID
  const travelMap = useMemo(() => {
    const map = new Map<string, Travel>();
    travels.forEach((travel) => {
      if (travel.id) {
        map.set(travel.id, travel);
      }
    });
    return map;
  }, [travels]);

  // Apply custom filter
  const filteredTravels = useMemo(() => {
    if (!travelFilter) return travels;
    return travels.filter(travelFilter);
  }, [travels, travelFilter]);

  // Build option items (shared between desktop Select and mobile modal)
  const travelOptionItems = useMemo(() => {
    const options: {
      id: string;
      label: string;
      details?: string;
      isActive?: boolean;
      isAllOption?: boolean;
      travel?: Travel;
    }[] = [];

    if (showAllOption) {
      options.push({
        id: ALL_TRAVELS_VALUE,
        label: defaultAllOptionLabel,
        isAllOption: true,
      });
    }

    filteredTravels.forEach((travel) => {
      if (travel.id) {
        options.push({
          id: travel.id,
          label: getTravelDisplayName(travel, t),
          details: showDetails ? getTravelDetails(travel) : undefined,
          isActive: activeTravel?.id === travel.id,
          travel,
        });
      }
    });

    return options;
  }, [
    filteredTravels,
    showAllOption,
    defaultAllOptionLabel,
    showDetails,
    activeTravel?.id,
    t,
  ]);

  // Desktop option data (for Mantine Select)
  const travelOptions = useMemo((): TravelOptionData[] => {
    return travelOptionItems.map((item) => ({
      value: item.id,
      label: item.label,
      details: item.details,
      isActive: item.isActive,
      travelType: item.travel?.travelType,
    }));
  }, [travelOptionItems]);

  // Auto-selection logic
  useEffect(() => {
    // Skip if already selected or already auto-selected
    if (value || hasAutoSelectedRef.current) {
      return;
    }

    // Skip if still loading initial data
    if (loading) {
      return;
    }

    // Skip if no travels loaded yet (and showAllOption is not enabled)
    if (travels.length === 0 && !showAllOption) {
      return;
    }

    let selectedId: string | null = null;

    // Priority 1: Preselect active travel if enabled and available
    if (preselectActiveTravel && hasActiveTravel && activeTravel?.id) {
      // Check if active travel matches the carId filter (if carId is provided)
      const activeTravelCarId = activeTravel.carId || activeTravel.car?.id;
      const matchesCarFilter = !carId || activeTravelCarId === carId;

      if (matchesCarFilter) {
        selectedId = activeTravel.id;
      }
    }

    // Priority 2: If required and still no selection, use first travel
    if (!selectedId && required && travels.length > 0) {
      selectedId = travels[0].id || null;
    }

    // Apply auto-selection
    if (selectedId) {
      hasAutoSelectedRef.current = true;
      const travel = travelMap.get(selectedId) || getTravelById(selectedId);
      onChange?.(name, selectedId, travel);
    }
  }, [
    value,
    loading,
    travels,
    preselectActiveTravel,
    hasActiveTravel,
    activeTravel,
    carId,
    required,
    showAllOption,
    travelMap,
    getTravelById,
    onChange,
    name,
  ]);

  // Handle onChange with travel object lookup
  const handleChange = useCallback(
    (selectedValue: string | null) => {
      if (selectedValue === ALL_TRAVELS_VALUE) {
        onChange?.(name, null, undefined);
      } else if (selectedValue) {
        const travel = travelMap.get(selectedValue) || getTravelById(selectedValue);
        onChange?.(name, selectedValue, travel);
      } else {
        onChange?.(name, null, undefined);
      }
    },
    [name, travelMap, getTravelById, onChange]
  );

  // Handle modal selection (mobile)
  const handleModalSelect = useCallback(
    (travelId: string | null, travel?: Travel) => {
      onChange?.(name, travelId, travel);
    },
    [name, onChange]
  );

  // Handle clear (mobile)
  const handleClear = useCallback(() => {
    onChange?.(name, null, undefined);
  }, [name, onChange]);

  // Get display text for the selected travel
  const selectedDisplayText = useMemo(() => {
    if (value === null && showAllOption) return defaultAllOptionLabel;
    if (!value) return "";
    const travel = travelMap.get(value) || getTravelById(value);
    if (!travel) return "";
    return getTravelDisplayName(travel, t);
  }, [value, showAllOption, defaultAllOptionLabel, travelMap, getTravelById, t]);

  // Render option for dropdown (desktop)
  const renderOption: SelectProps["renderOption"] = useCallback(
    ({ option }: { option: TravelOptionData }) => {
      const optionData = option as TravelOptionData;

      if (optionData.value === ALL_TRAVELS_VALUE) {
        return (
          <Group gap="sm" wrap="nowrap">
            <AppIcon iconName="Route" size={24} />
            <Text size="md" fw={500}>
              {optionData.label}
            </Text>
          </Group>
        );
      }

      return (
        <Group gap="sm" wrap="nowrap" align="flex-start">
          <Box pt={2}>
            <AppIcon iconName="Route" size={24} />
          </Box>
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Group gap="xs" wrap="nowrap">
              <Text size="md" fw={500} lineClamp={1} style={{ flex: 1 }}>
                {optionData.label}
              </Text>
              {optionData.isActive && (
                <Badge size="xs" color="green" variant="light">
                  {t("common:selectors.travel.active")}
                </Badge>
              )}
            </Group>
            {optionData.details && (
              <Text size="sm" c="dimmed" lineClamp={1}>
                {optionData.details}
              </Text>
            )}
          </Stack>
        </Group>
      );
    },
    [t]
  );

  // Memoize left section icon
  const leftSection = useMemo(() => {
    return <AppIcon iconName="Route" size={24} />;
  }, []);

  // Determine current state
  const isEmpty = !loading && travels.length === 0;

  // ==========================================
  // Mobile: Read-only input + full-screen modal
  // ==========================================
  if (isMobile) {
    const isDisabled =
      disabled || (loading && travels.length === 0) || (isEmpty && !showAllOption);

    const mobilePlaceholder =
      loading && travels.length === 0
        ? defaultLoadingPlaceholder
        : isEmpty && !showAllOption
          ? defaultEmptyText
          : defaultPlaceholder;

    const mobileRightSection = (() => {
      if (loading && travels.length === 0) {
        return <LoadingIndicator size={size} color="brand" />;
      }
      if (selectedDisplayText && !required && !disabled) {
        return (
          <CloseButton
            size={size}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            aria-label={t("common:form.clearSelection")}
          />
        );
      }
      return <AppIcon iconName="ChevronDown" size={18} />;
    })();

    return (
      <Box>
        <TextInput
          label={defaultLabel}
          placeholder={mobilePlaceholder}
          value={selectedDisplayText}
          readOnly
          onClick={() => {
            if (!isDisabled) {
              setModalOpened(true);
            }
          }}
          leftSection={leftSection}
          rightSection={mobileRightSection}
          rightSectionPointerEvents={
            selectedDisplayText && !required && !disabled ? "auto" : "none"
          }
          error={
            lastError && !loading && travels.length === 0
              ? error || t("common:messages.loadError")
              : error
          }
          required={required}
          disabled={isDisabled}
          size={size}
          styles={{
            input: {
              cursor: isDisabled ? "not-allowed" : "pointer",
              caretColor: "transparent",
            },
          }}
        />

        <TravelPickerModal
          opened={modalOpened}
          onClose={() => setModalOpened(false)}
          value={value}
          onSelect={handleModalSelect}
          options={travelOptionItems}
          showAllOption={showAllOption}
          allOptionLabel={defaultAllOptionLabel}
        />
      </Box>
    );
  }

  // ==========================================
  // Desktop: Original Select behavior
  // ==========================================

  // Memoize select state
  const selectState = useMemo(() => {
    if (loading && travels.length === 0) {
      return {
        data: [] as TravelOptionData[],
        placeholder: defaultLoadingPlaceholder,
        isDisabled: true,
        showLoading: true,
        errorMessage: undefined as string | undefined,
      };
    }

    if (lastError && !loading && travels.length === 0) {
      return {
        data: [] as TravelOptionData[],
        placeholder: t("common:errors.generic"),
        isDisabled: true,
        showLoading: false,
        errorMessage: error || t("common:messages.loadError"),
      };
    }

    if (isEmpty && !showAllOption) {
      return {
        data: [] as TravelOptionData[],
        placeholder: defaultEmptyText,
        isDisabled: true,
        showLoading: false,
        errorMessage: undefined as string | undefined,
      };
    }

    return {
      data: travelOptions,
      placeholder: defaultPlaceholder,
      isDisabled: disabled || loading,
      showLoading: loading && travels.length > 0,
      errorMessage: undefined as string | undefined,
    };
  }, [
    loading,
    travels.length,
    lastError,
    isEmpty,
    showAllOption,
    travelOptions,
    defaultLoadingPlaceholder,
    defaultEmptyText,
    defaultPlaceholder,
    disabled,
    error,
    t,
  ]);

  // Memoize right section
  const rightSection = useMemo(() => {
    if (selectState.showLoading) {
      return <LoadingIndicator size={size} color="brand" />;
    }
    return selectProps.rightSection;
  }, [selectState.showLoading, size, selectProps.rightSection]);

  // Determine display value (handle "All Travels" case)
  const displayValue = useMemo(() => {
    if (value === null && showAllOption) {
      return ALL_TRAVELS_VALUE;
    }
    return value ?? null;
  }, [value, showAllOption]);

  // Combobox props for portal rendering
  const comboboxPropsPortal = useMemo(() => {
    if (!withinPortal) return undefined;
    return {
      withinPortal: true,
      zIndex: 1000,
    };
  }, [withinPortal]);

  return (
    <Box>
      <Select
        {...selectProps}
        label={defaultLabel}
        size={size}
        value={displayValue}
        onChange={handleChange}
        placeholder={selectState.placeholder}
        clearable={!required && !showAllOption}
        required={required}
        allowDeselect={!required}
        disabled={selectState.isDisabled}
        error={selectState.errorMessage || error}
        data={selectState.data}
        rightSection={rightSection}
        leftSection={leftSection}
        renderOption={renderOption}
        nothingFoundMessage={t("common:form.noOptions")}
        comboboxProps={{
          ...(comboboxProps || {}),
          ...(comboboxPropsPortal || {}),
        }}
      />
    </Box>
  );
};

export const TravelSelector = React.memo(TravelSelectorComponent);